import { useState, useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import classNames from "classnames";
import CiroModal from "../../../shared/CiroModal";
import CiroButton, { CiroButtonStyleEnum } from "../../../shared/CiroButton";
import CiroTextInput from "../../../shared/CiroTextInput";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SalesforceIcon from "../../../../assets/img/icons/SalesforceIcon";
import HubspotIcon from "../../../../assets/img/icons/HubspotIcon";
import { IntegrationEnum } from "../../../../__generated__/graphql";
import { getDomainFromUrl } from "../../../../utils/formatters";

const IntegrationModalOAuth_OauthAuthorizationUrl = gql`
  query IntegrationModalOAuth_OauthAuthorizationUrl(
    $integration: CrmIntegrationEnum!
    $redirectUri: String!
    $salesforceDomain: String
  ) {
    oauthAuthorizationUrl(
      integration: $integration
      redirectUri: $redirectUri
      salesforceDomain: $salesforceDomain
    ) {
      url
      message
    }
  }
`;

interface IOAuthFlowProps {
  integration: IntegrationEnum.Salesforce | IntegrationEnum.HubSpot;
  onClose: () => void;
  onSuccess?: () => void;
  userAccountQuery: {
    data: any;
    startPolling: (ms: number) => void;
    stopPolling: () => void;
  };
}

const IntegrationModalOAuth = ({
  integration,
  onClose,
  onSuccess,
  userAccountQuery,
}: IOAuthFlowProps) => {
  const [domain, setDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasTriggeredSuccess = useRef(false);

  const [getOAuthUrl] = useLazyQuery(
    IntegrationModalOAuth_OauthAuthorizationUrl,
    {
      onCompleted: (data) => {
        setIsLoading(false);
        if (data.oauthAuthorizationUrl.url) {
          window.open(
            data.oauthAuthorizationUrl.url,
            "_blank",
            "noopener,noreferrer",
          );
          // Start polling for CRM connection after opening OAuth window
          userAccountQuery.startPolling(1000);
        } else if (data.oauthAuthorizationUrl.message) {
          setError(data.oauthAuthorizationUrl.message);
        }
      },
      onError: (error) => {
        setIsLoading(false);
        setError(error.message);
      },
    },
  );

  useEffect(() => {
    const crmIntegration =
      userAccountQuery.data?.userAccount?.org?.organizationCrmIntegration;
    if (
      crmIntegration &&
      crmIntegration.integration === integration &&
      crmIntegration.relink_needed === false &&
      !hasTriggeredSuccess.current
    ) {
      hasTriggeredSuccess.current = true;
      userAccountQuery.stopPolling();
      if (onSuccess) {
        onSuccess();
      } else {
        onClose();
      }
    }

    return () => {
      userAccountQuery.stopPolling();
    };
  }, [
    userAccountQuery.data,
    integration,
    onSuccess,
    onClose,
    userAccountQuery,
  ]);

  const handleSubmit = () => {
    if (integration === IntegrationEnum.Salesforce && !domain.trim()) {
      setError("Please enter your Salesforce domain");
      return;
    }

    setIsLoading(true);
    setError(null);

    const redirectUri = `${window.location.origin}/oauth/callback`;

    // Need this because the React app is not hosted at the same port as the web server
    const finalRedirectUri = redirectUri.includes("localhost:3000")
      ? "http://localhost:3001/oauth/callback"
      : redirectUri;

    getOAuthUrl({
      variables: {
        integration,
        redirectUri: finalRedirectUri,
        salesforceDomain:
          integration === IntegrationEnum.Salesforce
            ? getDomainFromUrl(domain)
            : undefined,
      },
    });
  };

  const integrationName =
    integration === IntegrationEnum.Salesforce ? "Salesforce" : "HubSpot";
  const IntegrationIcon =
    integration === IntegrationEnum.Salesforce ? SalesforceIcon : HubspotIcon;

  return (
    <CiroModal isOpen={true} onClose={onClose}>
      <div className={classNames("mb-5")}>
        <div className={classNames("flex", "justify-between", "items-center")}>
          <div
            className={classNames(
              "text-xl",
              "font-bold",
              "pb-2",
              "text-neutral-900",
            )}
          >
            Connect CRM
          </div>
          <div>
            <CiroButton
              style={CiroButtonStyleEnum.UNSTYLED}
              analyticsField={`Close ${integrationName} OAuth Modal`}
              onClick={onClose}
            >
              <XMarkIcon className={classNames("h-6", "w-6")} />
            </CiroButton>
          </div>
        </div>

        <div className={classNames("flex", "items-center", "mb-4")}>
          <IntegrationIcon />
          <span className={classNames("ml-2", "text-lg", "font-medium")}>
            {integrationName}
          </span>
        </div>

        {integration === IntegrationEnum.Salesforce && (
          <div className={classNames("mb-4")}>
            <CiroTextInput
              label="Enter your Salesforce domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourcompany.my.salesforce.com"
              wrapperClassName="mb-2"
            />
            <p className={classNames("text-xs", "text-neutral-500")}>
              Example: yourcompany.my.salesforce.com
            </p>
          </div>
        )}

        {integration === IntegrationEnum.HubSpot && (
          <div className={classNames("mb-4")}>
            <p className={classNames("text-sm", "text-neutral-600")}>
              Click "Go to HubSpot" to connect your HubSpot account to Ciro.
            </p>
          </div>
        )}

        {error && (
          <div
            className={classNames(
              "mb-4",
              "p-3",
              "bg-red-50",
              "border",
              "border-red-200",
              "rounded-md",
            )}
          >
            <p className={classNames("text-sm", "text-red-700")}>{error}</p>
          </div>
        )}

        <div className={classNames("flex", "justify-end", "gap-2")}>
          <CiroButton
            style={CiroButtonStyleEnum.SECONDARY}
            onClick={onClose}
            analyticsField={`Cancel ${integrationName} OAuth`}
          >
            Cancel
          </CiroButton>
          <CiroButton
            style={CiroButtonStyleEnum.LOUD}
            onClick={handleSubmit}
            disabled={
              isLoading ||
              (integration === IntegrationEnum.Salesforce && !domain.trim())
            }
            analyticsField={`Go to ${integrationName} OAuth`}
          >
            {isLoading ? "Loading..." : `Go to ${integrationName}`}
          </CiroButton>
        </div>
      </div>
    </CiroModal>
  );
};

export default IntegrationModalOAuth;
